import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Well } from "@zendeskgarden/react-notifications";
import { Tag } from "@zendeskgarden/react-tags";
import { Button } from "@zendeskgarden/react-buttons";
import { Notification } from "@zendeskgarden/react-notifications";
import { useNavigate, useLocation } from "react-router-dom";
import { adminApi } from "../../api";
import { AdminApi } from "../../api/admin";
import UserManagement from './UserManagement';
import { User, useAuth } from "../../utils/auth";
import { Case } from "../../api/cases";
import FilterableTable, { Column } from "../common/FilterableTable";
import DashboardLayout from "../common/DashboardLayout";
import Profile from "../common/Profile";
import HelpSupport from "../common/HelpSupport";
import ErrorBoundary from "../common/ErrorBoundary";
import Accordion from "../common/Accordion";
import { DashboardIcon, UsersIcon, SettingsIcon, ProfileIcon, HelpSupportIcon } from "../common/Icons";

// Helper function to safely render diagnosis data
const safeDiagnosisRender = (diagnosis: any): string => {
  if (typeof diagnosis === 'string') return diagnosis;
  if (typeof diagnosis === 'object' && diagnosis?.label) return diagnosis.label;
  if (typeof diagnosis === 'object' && diagnosis?.name) return diagnosis.name;
  return 'N/A';
};

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
`;

const StatCard = styled(Well)`
  padding: 24px;
  text-align: center;
  border: none;
  background: white;
  box-shadow: none;
  margin: 8px;
`;

const StatNumber = styled.div`
  font-size: 32px;
  font-weight: bold;
  color: #8D4F1B;
  margin-bottom: 8px;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const StatLabel = styled.div`
  color: #68737d;
  font-size: 14px;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const SectionTitle = styled.h2`
  color: #2f3941;
  margin-bottom: 16px;
  font-size: 20px;
  font-weight: 600;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const UserManagementContainer = styled.div`
  margin-bottom: 32px;
`;

const UserTabContainer = styled.div`
  border-bottom: 1px solid #e9ebed;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const UserTabList = styled.div`
  display: flex;
  gap: 0;
`;

const UserTabButton = styled.button<{ isActive: boolean }>`
  padding: 12px 24px;
  border: none;
  background: none;
  border-bottom: 2px solid ${props => props.isActive ? '#8D4F1B' : 'transparent'};
  color: ${props => props.isActive ? '#8D4F1B' : '#68737d'};
  font-weight: ${props => props.isActive ? '600' : '400'};
  cursor: pointer;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover {
    color: #8D4F1B;
  }
`;

const AddUserButton = styled(Button)`
  background-color: #8D4F1B !important;
  border-color: #8D4F1B !important;
  color: white !important;
  
  &:hover {
    background-color: #703815 !important;
    border-color: #703815 !important;
  }
  
  &:active {
    background-color: #5a2c10 !important;
    border-color: #5a2c10 !important;
  }
`;

const TableSection = styled.div`
  margin-top: 32px;
`;

const FullWidthContainer = styled.div`
  width: 100%;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  min-height: 400px;
`;

const ModalField = styled.div`
  margin-bottom: 16px;
`;

const ModalLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 600;
  color: #2f3941;
  font-size: 14px;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  font-size: 14px;
  color: #2f3941;
  
  &:focus {
    outline: none;
    border-color: #8D4F1B;
  }
`;

const AddButton = styled(Button)`
  background-color: #8D4F1B !important;
  border-color: #8D4F1B !important;
  color: white !important;
  
  &:hover {
    background-color: #703815 !important;
    border-color: #703815 !important;
  }
  
  &:active {
    background-color: #5a2c10 !important;
    border-color: #5a2c10 !important;
  }
`;

const CancelButton = styled(Button)`
  background-color: #f8f9fa !important;
  border-color: #d8dcde !important;
  color: #68737d !important;
  
  &:hover {
    background-color: #e9ecef !important;
    border-color: #ced4da !important;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9000;
  padding: 20px;
  
  /* Hide scrollbars */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  
  &::-webkit-scrollbar {
    display: none; /* WebKit */
  }
  
  @media (max-width: 768px) {
    padding: 0;
    align-items: flex-start;
  }
  
  @media (max-width: 480px) {
    padding: 0;
    align-items: flex-start;
  }
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 8px;
  width: 90vw;
  max-width: 1400px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  
  /* Hide scrollbars */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  
  &::-webkit-scrollbar {
    display: none; /* WebKit */
  }
  
  @media (max-width: 768px) {
    width: 100vw;
    max-height: 100vh;
    border-radius: 0;
  }
  
  @media (max-width: 480px) {
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const ModalHeader = styled.div`
  background: #ffffff;
  padding: 24px;
  border-bottom: 1px solid #e9ebed;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    position: sticky;
    top: 0;
    z-index: 10;
    border-bottom: 2px solid #e9ebed;
  }
`;

const ModalTitle = styled.h2`
  color: #2f3941;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  color: #68737d;
  cursor: pointer;
  font-size: 24px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  
  &:hover {
    background: #f8f9fa;
    color: #2f3941;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const ModalFooter = styled.div`
  background: #ffffff;
  padding: 24px;
  border-top: 1px solid #e9ebed;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    position: sticky;
    bottom: 0;
    z-index: 10;
  }
`;

// Case Details Modal Styled Components
const CaseDetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const CaseDetailLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CaseDetailRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CaseImage = styled.div`
  width: 100%;
  height: 300px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 8px;
  border: 1px solid #e9ebed;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #68737d;
  font-size: 16px;
  background-color: #f8f9fa;
`;

const DetailSection = styled.div`
  background: #ffffff;
  border: 1px solid #e9ebed;
  border-radius: 8px;
  padding: 20px;
`;

const DetailItem = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #68737d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const DetailValue = styled.div`
  font-size: 14px;
  color: #2f3941;
  line-height: 1.4;
`;

const DetailValueLarge = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2f3941;
  line-height: 1.4;
`;

// Status tag component matching cases styling
const StatusTag = styled.div<{ status: 'completed' | 'pending' | 'reviewing' }>`
  background: ${props => {
    switch (props.status) {
      case 'completed': return '#d4edda';
      case 'pending': return '#fff3cd';
      case 'reviewing': return '#cce5ff';
      default: return '#e9ecef';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed': return '#155724';
      case 'pending': return '#856404';
      case 'reviewing': return '#0c5aa6';
      default: return '#495057';
    }
  }};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 590;
  display: inline-block;
  
  @media (max-width: 480px) {
    padding: 3px 6px;
    font-size: 11px;
  }
`;

const RoleTag = styled.div<{ role: 'admin' | 'physician' | 'specialist' | string }>`
  background: ${props => {
    switch (props.role.toLowerCase()) {
      case 'admin': return '#ffe6e6';
      case 'physician': return '#e6f3ff';
      case 'specialist': return '#f0e6ff';
      default: return '#f0f0f0';
    }
  }};
  color: ${props => {
    switch (props.role.toLowerCase()) {
      case 'admin': return '#cc0000';
      case 'physician': return '#0066cc';
      case 'specialist': return '#7a00cc';
      default: return '#666666';
    }
  }};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 590;
  display: inline-block;
  
  @media (max-width: 480px) {
    padding: 3px 6px;
    font-size: 11px;
  }
`;

// AI Differential Cards styling (similar to cases component)
const DifferentialCard = styled.div`
  background: #ffffff;
  border: 1px solid #e9ebed;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DifferentialHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const DifferentialName = styled.div`
  font-weight: 600;
  color: #2f3941;
  font-size: 14px;
`;

const DifferentialPercentage = styled.div`
  font-weight: 600;
  color: #8D4F1B;
  font-size: 14px;
`;

const DifferentialDetails = styled.div`
  margin-top: 8px;
`;

const DifferentialDescription = styled.div`
  color: #68737d;
  font-size: 13px;
  line-height: 1.4;
  margin-top: 8px;
`;

const BackToMyCasesButton = styled.button`
  background: none;
  border: none;
  color: #68737d;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  
  &:hover {
    color: #2f3941;
  }
`;

const MobileCloseButton = styled.button`
  background: none;
  border: none;
  color: #68737d;
  cursor: pointer;
  font-size: 24px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  
  &:hover {
    background: #f8f9fa;
    color: #2f3941;
  }
`;

const Title = styled.h1`
  font-family: 'SF Pro Display', sans-serif;
  font-weight: 400;
  font-size: 24px;
  line-height: 30px;
  color: #2f3941;
  margin: 0;
  letter-spacing: 0.352px;
`;

const ViewCaseButton = styled.button`
  background: none;
  border: none;
  color: #8D4F1B;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  text-decoration: underline;
  
  &:hover {
    color: #703815;
  }
`;

// Filter Controls Styled Components
const FilterSection = styled.div`
  background: #ffffff;
  border: 1px solid #e9ebed;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;
  
  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #68737d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  font-size: 14px;
  color: #2f3941;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #8D4F1B;
    box-shadow: 0 0 0 2px rgba(141, 79, 27, 0.2);
  }
`;

const FilterInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  font-size: 14px;
  color: #2f3941;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #8D4F1B;
    box-shadow: 0 0 0 2px rgba(141, 79, 27, 0.2);
  }
`;

const PaginationInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding: 16px 0;
  border-top: 1px solid #e9ebed;
  font-size: 14px;
  color: #68737d;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PageButton = styled.button<{ isActive?: boolean; disabled?: boolean }>`
  padding: 8px 12px;
  border: 1px solid ${props => props.isActive ? '#8D4F1B' : '#d8dcde'};
  background: ${props => props.isActive ? '#8D4F1B' : 'white'};
  color: ${props => props.isActive ? 'white' : props.disabled ? '#d8dcde' : '#2f3941'};
  border-radius: 4px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  
  &:hover:not(:disabled) {
    background: ${props => props.isActive ? '#703815' : '#f8f9fa'};
    border-color: ${props => props.isActive ? '#703815' : '#ced4da'};
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [cases, setCases] = useState<Case[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [userStats, setUserStats] = useState({ totalUsers: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'users' | 'settings' | 'profile' | 'help'>('dashboard');
  const [activeUserTab, setActiveUserTab] = useState<'physicians' | 'specialists' | 'admins'>('physicians');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserType, setNewUserType] = useState<'admin' | 'specialist'>('admin');
  const [newUserData, setNewUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  });
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [showCaseDetailModal, setShowCaseDetailModal] = useState(false);
  
  // Search state
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [searchValue, setSearchValue] = useState('');
  
  // Pagination and filtering state
  const [casePagination, setCasePagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCases: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10,
    showing: 0
  });
  const [caseFilters, setCaseFilters] = useState({
    status: 'all',
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    async function fetchUsers() {
      try {
        const usersRes = await adminApi.getAllUsers({ page: 1, limit: 1 }); // Just get total count
        console.log('Users response:', usersRes);
        const payload = usersRes.data || usersRes;
        const pagination = payload.pagination || {};
        setUserStats({ totalUsers: pagination.totalUsers || 0 });
        // Don't set users here since we're not using them in AdminDashboard anymore
      } catch (e) {
        console.error("Failed to fetch users:", e);
        setUserStats({ totalUsers: 0 });
      }
    }
    fetchUsers();
  }, []);

  // Separate function to fetch cases with pagination and filters
  const fetchCases = async (page: number = 1, filters: any = caseFilters) => {
    setLoading(true);
    try {
      // Build parameters for API call
      const params = {
        page: page,
        limit: 15, // Cases per page
        status: filters.status === 'all' ? undefined : filters.status,
        search: filters.search || undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder as 'asc' | 'desc'
      };

      const casesRes = await adminApi.getAllCases(params);
      console.log('Cases response:', casesRes);
      
      // Handle the new API response structure
      const responseData = casesRes.data;
      const casesData = responseData.cases || [];
      const pagination = responseData.pagination || {};
      
      // Transform cases data to match the expected format
      const transformedCases = casesData.map((caseItem: any) => ({
        _id: caseItem.id || caseItem._id,
        id: caseItem.id || caseItem._id, // For display purposes
        images: Array.isArray(caseItem.images) ? caseItem.images : [{url: ''}], // Handle images count or array
        age: caseItem.patient?.age || 0,
        sex: caseItem.patient?.sex || 'Male',
        symptoms: caseItem.patient?.symptoms || '',
        doctorDiagnosis: typeof caseItem.diagnosis?.doctor === 'string' 
          ? caseItem.diagnosis.doctor 
          : (typeof caseItem.diagnosis?.doctor === 'object' && caseItem.diagnosis.doctor?.label)
            ? caseItem.diagnosis.doctor.label 
            : 'N/A',
        doctorNote: caseItem.doctorNote || '',
        patientHistory: caseItem.patientHistory || '',
        diagnosisStatus: caseItem.diagnosis?.status || 'pending',
        predictedDiagnoses: caseItem.diagnosis?.ai || [],
        createdBy: caseItem.doctor || caseItem.createdBy || '',
        reviewedBy: caseItem.reviewedBy || '',
        reviewNote: caseItem.reviewNote || '',
        reviewStatus: caseItem.diagnosis?.status || 'pending',
        createdAt: caseItem.dates?.created || caseItem.createdAt || new Date().toISOString(),
        updatedAt: caseItem.dates?.updated || caseItem.updatedAt || new Date().toISOString(),
        // Additional properties for table display
        reviewedDiagnosis: typeof caseItem.diagnosis?.primary === 'string' 
          ? caseItem.diagnosis.primary 
          : (typeof caseItem.diagnosis?.primary === 'object' && caseItem.diagnosis.primary?.label)
            ? caseItem.diagnosis.primary.label 
            : 'Pending',
        reviewed: (caseItem.diagnosis?.status) === 'approved' || false,
        patient: caseItem.patient,
        doctor: caseItem.doctor,
      } as Case & { 
        id: string; 
        reviewedDiagnosis: string; 
        reviewed: boolean; 
        patient: any; 
        doctor: any; 
      }));
      
      setCases(transformedCases);
      setCasePagination({
        currentPage: pagination.currentPage || 1,
        totalPages: pagination.totalPages || 1,
        totalCases: pagination.totalCases || 0,
        hasNextPage: pagination.hasNextPage || false,
        hasPrevPage: pagination.hasPrevPage || false,
        limit: (pagination as any).limit || 15,
        showing: (pagination as any).showing || transformedCases.length
      });
      // Update filters from API response if available
      if ((responseData as any).filters) {
        setCaseFilters((responseData as any).filters);
      }
    } catch (e) {
      console.error("Failed to fetch cases:", e);
      setCases([]);
    }
    setLoading(false);
  };

  // Initial load of cases
  useEffect(() => {
    fetchCases(1, caseFilters);
    setSearchValue(caseFilters.search);
  }, []);

  // Update search value when filters change from other sources
  useEffect(() => {
    setSearchValue(caseFilters.search);
  }, [caseFilters.search]);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Determine active tab from URL
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/users')) {
      setActiveTab('users');
    } else if (path.includes('/settings')) {
      setActiveTab('settings');
    } else if (path.includes('/profile')) {
      setActiveTab('profile');
    } else if (path.includes('/help')) {
      setActiveTab('help');
    } else {
      setActiveTab('dashboard');
    }
  }, [location.pathname]);

  const stats = {
    totalUsers: loading ? 0 : userStats.totalUsers,
    casesAnalyzed: loading ? 0 : casePagination.totalCases || cases.length,
    pendingReviews: loading ? 0 : cases.filter(c => c.reviewStatus && (c.reviewStatus.toLowerCase() === 'pending' || c.reviewStatus.toLowerCase() === 'ready')).length,
    accuracyRate: (() => {
      if (loading || cases.length === 0) return 0;
      // Use diagnosis status from new schema
      const approved = cases.filter(c => c.diagnosisStatus && c.diagnosisStatus.toLowerCase() === 'approved').length;
      const rejected = cases.filter(c => c.diagnosisStatus && c.diagnosisStatus.toLowerCase() === 'rejected').length;
      const total = approved + rejected;
      if (total === 0) return 0;
      return Math.round((approved / total) * 100);
    })(),
  };

  // Filter users based on selected tab
  const getFilteredUsers = () => {
    switch (activeUserTab) {
      case 'physicians':
        return users.filter(user => user.role === 'physician');
      case 'specialists':
        return users.filter(user => user.role === 'specialist');
      case 'admins':
        return users.filter(user => user.role === 'admin' || user.role === 'super-admin');
      default:
        return users;
    }
  };

  const getUserTabTitle = () => {
    switch (activeUserTab) {
      case 'physicians':
        return 'Physicians';
      case 'specialists':
        return 'Specialists';
      case 'admins':
        return 'Administrators';
      default:
        return 'All Users';
    }
  };

  const handleAddUser = (userType: 'admin' | 'specialist') => {
    setNewUserType(userType);
    setNewUserData({
      firstName: '',
      lastName: '',
      email: '',
      password: ''
    });
    setShowAddUserModal(true);
  };

  const handleCloseModal = () => {
    setShowAddUserModal(false);
    setModalLoading(false);
  };

  const handleViewCaseDetails = (caseId: string) => {
    setSelectedCaseId(caseId);
    setShowCaseDetailModal(true);
  };

  const handleCloseCaseDetailModal = () => {
    setShowCaseDetailModal(false);
    setSelectedCaseId(null);
  };

  // Pagination and filtering handlers
  const handlePageChange = (page: number) => {
    fetchCases(page, caseFilters);
  };

  const handleFilterChange = (newFilters: any) => {
    const updatedFilters = { ...caseFilters, ...newFilters };
    setCaseFilters(updatedFilters);
    fetchCases(1, updatedFilters); // Reset to page 1 when filters change
  };

  const handleSearchChange = (search: string) => {
    // Update the input value immediately
    setSearchValue(search);
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      handleFilterChange({ search });
    }, 500); // 500ms delay
    
    setSearchTimeout(timeout);
  };

  const handleStatusFilter = (status: string) => {
    handleFilterChange({ status });
  };

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    handleFilterChange({ sortBy, sortOrder });
  };

  const handleCreateUser = async () => {
    setModalLoading(true);
    try {
      if (newUserType === 'admin') {
        await adminApi.createAdmin({
          firstName: newUserData.firstName,
          lastName: newUserData.lastName,
          email: newUserData.email,
          password: newUserData.password
        });
      } else {
        await adminApi.createSpecialist({
          firstName: newUserData.firstName,
          lastName: newUserData.lastName,
          email: newUserData.email,
          password: newUserData.password,
          specialty: 'General', // Default specialization
          institution: 'N/A', // Default institution
          professionalRegistration: 'N/A' // Default license number
        });
      }
      
      // Refresh users list
      const usersRes = await adminApi.getAllUsers();
      setUsers(usersRes.data.users || usersRes.data || []);
      
      handleCloseModal();
    } catch (error: any) {
      console.error('Failed to create user:', error);
    }
    setModalLoading(false);
  };

  const handleNavigation = (tabId: string) => {
    const tab = tabId as 'dashboard' | 'users' | 'settings' | 'profile' | 'help';
    setActiveTab(tab);
    switch (tab) {
      case 'users':
        navigate('/admin/users');
        break;
      case 'settings':
        navigate('/admin/settings');
        break;
      case 'profile':
        navigate('/admin/profile');
        break;
      case 'help':
        navigate('/admin/help');
        break;
      default:
        navigate('/admin');
        break;
    }
  };

  // Define table columns
  const userColumns: Column[] = [
    { key: 'name', title: 'Name' },
    { key: 'email', title: 'Email' },
    { 
      key: 'role', 
      title: 'Role',
      render: (value) => (
        <RoleTag role={value}>
          {value}
        </RoleTag>
      )
    },
    { 
      key: 'status', 
      title: 'Status',
      render: (value) => (
        <StatusTag status={value === 'Active' ? 'completed' : 'pending'}>
          {value}
        </StatusTag>
      )
    },
    { key: 'lastLogin', title: 'Last Login' }
  ];

  const caseColumns: Column[] = [
   
    { 
      key: 'doctorDiagnosis', 
      title: 'Physician Diagnosis',
      render: (value) => safeDiagnosisRender(value)
    },
    { 
      key: 'reviewedDiagnosis', 
      title: 'Reviewer Diagnosis',
      render: (value) => safeDiagnosisRender(value)
    },
    { 
      key: 'reviewed', 
      title: 'Status',
      render: (value) => (
        <StatusTag status={value ? 'completed' : 'pending'}>
          {value ? 'Reviewed' : 'Pending'}
        </StatusTag>
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, record: any) => (
        <ViewCaseButton
          onClick={() => handleViewCaseDetails(record._id || record.id)}
        >
          View Details
        </ViewCaseButton>
      )
    }
  ];

  const getPageTitle = () => {
    switch (activeTab) {
      case 'users':
        return 'User Management';
      case 'settings':
        return 'System Settings';
      case 'profile':
        return 'Profile';
      case 'help':
        return 'Help & Support';
      default:
        return 'Admin Dashboard';
    }
  };

  const getPageDescription = () => {
    switch (activeTab) {
      case 'users':
        return 'Manage user accounts, roles, and permissions across the platform.';
      case 'settings':
        return 'Configure system settings, preferences, and global configurations.';
      case 'profile':
        return 'Manage your profile information, security settings, and preferences.';
      case 'help':
        return 'Get help and support for using the DermAI platform.';
      default:
        return 'Welcome to the admin dashboard. Monitor and manage your platform.';
    }
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/admin'
    },
    {
      id: 'users',
      label: 'User Management',
      icon: <UsersIcon />,
      path: '/admin/users'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <SettingsIcon />,
      path: '/admin/settings'
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: <ProfileIcon />,
      path: '/admin/profile'
    },
    {
      id: 'help',
      label: 'Help & Support',
      icon: <HelpSupportIcon />,
      path: '/admin/help'
    }
  ];

  const renderMainContent = () => {
    switch (activeTab) {
      case 'users':
        return (
          <FullWidthContainer>
            <UserManagement />
          </FullWidthContainer>
        );
      case 'settings':
        return (
          <FullWidthContainer>
            <SectionTitle>System Settings</SectionTitle>
            <p>Settings configuration will be implemented here.</p>
          </FullWidthContainer>
        );
      case 'profile':
        return (
          <FullWidthContainer>
            <Profile />
          </FullWidthContainer>
        );
      case 'help':
        return (
          <FullWidthContainer>
            <HelpSupport />
          </FullWidthContainer>
        );
      default:
        return (
          <>
            {/* Statistics Overview */}
            <StatsGrid>
              <StatCard>
                <StatNumber>{stats.totalUsers}</StatNumber>
                <StatLabel>Total Users</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>{stats.casesAnalyzed}</StatNumber>
                <StatLabel>Cases Analyzed</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>{stats.pendingReviews}</StatNumber>
                <StatLabel>Pending Reviews</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>{stats.accuracyRate}%</StatNumber>
                <StatLabel>Accuracy Rate</StatLabel>
              </StatCard>
            </StatsGrid>

            {/* All Cases */}
            <TableSection>
              <SectionTitle>All Cases</SectionTitle>
              
              {/* Filter Controls */}
              <FilterSection>
                <FilterRow>
                  <FilterGroup>
                    <FilterLabel>Status Filter</FilterLabel>
                    <FilterSelect
                      value={caseFilters.status}
                      onChange={(e) => handleStatusFilter(e.target.value)}
                    >
                      <option value="all">All Cases</option>
                      <option value="pending">Pending</option>
                     
                      <option value="reviewed">Reviewed</option>
                      <option value="rejected">Rejected</option>
                    </FilterSelect>
                  </FilterGroup>
                  
                  <FilterGroup>
                    <FilterLabel>Search Cases</FilterLabel>
                    <FilterInput
                      type="text"
                      placeholder="Search by ID, diagnosis, or doctor..."
                      value={searchValue}
                      onChange={(e) => handleSearchChange(e.target.value)}
                    />
                  </FilterGroup>
                  
                  <FilterGroup>
                    <FilterLabel>Sort By</FilterLabel>
                    <FilterSelect
                      value={`${caseFilters.sortBy}-${caseFilters.sortOrder}`}
                      onChange={(e) => {
                        const [sortBy, sortOrder] = e.target.value.split('-');
                        handleSortChange(sortBy, sortOrder);
                      }}
                    >
                      <option value="createdAt-desc">Newest First</option>
                      <option value="createdAt-asc">Oldest First</option>
                      <option value="status-asc">Status A-Z</option>
                      <option value="status-desc">Status Z-A</option>
                    </FilterSelect>
                  </FilterGroup>
                </FilterRow>
              </FilterSection>

              <FilterableTable
                title=""
                columns={caseColumns}
                data={cases}
                loading={loading}
                searchPlaceholder=""
                searchFields={[]}
                showPagination={false} // We'll handle pagination manually
                itemsPerPage={casePagination.limit}
              />
              
              {/* Custom Pagination */}
              <PaginationInfo>
                <div>
                  Showing {casePagination.showing} of {casePagination.totalCases} cases
                </div>
                <PaginationControls>
                  <PageButton
                    disabled={!casePagination.hasPrevPage}
                    onClick={() => handlePageChange(casePagination.currentPage - 1)}
                  >
                    Previous
                  </PageButton>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(casePagination.totalPages, 5) }, (_, i) => {
                    const startPage = Math.max(1, casePagination.currentPage - 2);
                    const pageNumber = startPage + i;
                    if (pageNumber <= casePagination.totalPages) {
                      return (
                        <PageButton
                          key={pageNumber}
                          isActive={pageNumber === casePagination.currentPage}
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </PageButton>
                      );
                    }
                    return null;
                  })}
                  
                  <PageButton
                    disabled={!casePagination.hasNextPage}
                    onClick={() => handlePageChange(casePagination.currentPage + 1)}
                  >
                    Next
                  </PageButton>
                </PaginationControls>
              </PaginationInfo>
            </TableSection>
          </>
        );
    }
  };

  const CaseDetailModal = ({ caseId }: { caseId: string }) => {
    const caseData = cases.find(case_ => case_._id === caseId || (case_ as any).id === caseId);
    if (!caseData) return null;

    // Format date
    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };

    // Get the first image URL
    const imageUrl = caseData.images[0]?.url || '';

    // Get primary diagnosis from AI predictions
    const primaryDiagnosis = caseData.predictedDiagnoses?.[0];

    return (
      <ModalOverlay onClick={handleCloseCaseDetailModal}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>Case {caseData._id || (caseData as any).id}</ModalTitle>
            <ModalCloseButton onClick={handleCloseCaseDetailModal} aria-label="Close modal">
              ×
            </ModalCloseButton>
          </ModalHeader>
          
          <ModalBody>
            <BackToMyCasesButton onClick={handleCloseCaseDetailModal} aria-label="Back">
              ← 
              Back
            </BackToMyCasesButton>
            
            <CaseDetailGrid>
              <CaseDetailLeft>
                <CaseImage style={{ backgroundImage: imageUrl ? `url(${imageUrl})` : 'none' }}>
                  {!imageUrl && 'No Image Available'}
                </CaseImage>
                <DetailSection>
                  <DetailItem>
                    <DetailLabel>Submitted by:</DetailLabel>
                    <DetailValue>
                      {typeof caseData.createdBy === 'object' 
                        ? caseData.createdBy.name 
                        : caseData.createdBy || 'Unknown'
                      }
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Submission Date:</DetailLabel>
                    <DetailValue>{formatDate(caseData.createdAt)}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Patient Info:</DetailLabel>
                    <DetailValue>{caseData.age}y, {caseData.sex}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Clinical Information:</DetailLabel>
                    <DetailValue>{caseData.symptoms || 'No clinical notes provided'}</DetailValue>
                  </DetailItem>
                  {caseData.patientHistory && (
                    <DetailItem>
                      <DetailLabel>Patient History:</DetailLabel>
                      <DetailValue>{caseData.patientHistory}</DetailValue>
                    </DetailItem>
                  )}
                </DetailSection>
              </CaseDetailLeft>
              
              <CaseDetailRight>
                <DetailSection>
                  <DetailItem>
                    <DetailLabel>Physician Diagnosis:</DetailLabel>
                    <DetailValueLarge>
                      {caseData.doctorDiagnosis || 'Not provided'}
                    </DetailValueLarge>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>AI Differentials:</DetailLabel>
                    <DetailValue>
                      {caseData.predictedDiagnoses && caseData.predictedDiagnoses.length > 0 ? (
                        caseData.predictedDiagnoses.map((diagnosis, index) => (
                          <DifferentialCard key={index}>
                            <DifferentialHeader>
                              <DifferentialName>{safeDiagnosisRender(diagnosis)}</DifferentialName>
                              <DifferentialPercentage>
                                {Math.round((diagnosis.confidence || 0) )}%
                              </DifferentialPercentage>
                            </DifferentialHeader>
                            {diagnosis.reasoning && (
                              <DifferentialDetails>
                                <Accordion title="View Reasoning" defaultOpen={false}>
                                  <DifferentialDescription>
                                    {typeof diagnosis.reasoning === 'string' ? diagnosis.reasoning : 'No reasoning provided'}
                                  </DifferentialDescription>
                                </Accordion>
                              </DifferentialDetails>
                            )}
                          </DifferentialCard>
                        ))
                      ) : (
                        'No AI predictions available'
                      )}
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Diagnosis Status:</DetailLabel>
                    <DetailValue>{caseData.diagnosisStatus || 'Pending'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Review Status:</DetailLabel>
                    <DetailValue>
                      {caseData.reviewStatus === 'approved' ? 'Approved' : 
                       caseData.reviewStatus === 'rejected' ? 'Rejected' : 'Pending Review'}
                    </DetailValue>
                  </DetailItem>
                  {caseData.reviewNote && (
                    <DetailItem>
                      <DetailLabel>Review Notes:</DetailLabel>
                      <DetailValue>{caseData.reviewNote}</DetailValue>
                    </DetailItem>
                  )}
                  {caseData.reviewedBy && (
                    <DetailItem>
                      <DetailLabel>Reviewed By:</DetailLabel>
                      <DetailValue>
                        {typeof caseData.reviewedBy === 'object' 
                          ? caseData.reviewedBy.name 
                          : caseData.reviewedBy
                        }
                      </DetailValue>
                    </DetailItem>
                  )}
                </DetailSection>
              </CaseDetailRight>
            </CaseDetailGrid>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    );
  };

  return (
    <React.Fragment>
      <ErrorBoundary>
        <DashboardLayout
          title={getPageTitle()}
          userInfo={`${user?.name || 'Admin User'} • ${user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Administrator'}`}
          activeTab={activeTab}
          menuItems={menuItems}
          onTabChange={handleNavigation}
          breadcrumb={getPageTitle()}
          userName={user?.name || 'Admin User'}
          userType={user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Administrator'}
          pageDescription={getPageDescription()}
        >
          {renderMainContent()}
        </DashboardLayout>
      </ErrorBoundary>
      {showAddUserModal && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Add New {newUserType === 'admin' ? 'Administrator' : 'Specialist'}</ModalTitle>
              <ModalCloseButton onClick={handleCloseModal} aria-label="Close modal">
                ×
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <ModalField>
                <ModalLabel htmlFor="firstName">First Name</ModalLabel>
                <ModalInput
                  id="firstName"
                  value={newUserData.firstName}
                  onChange={(e) => setNewUserData({...newUserData, firstName: e.target.value})}
                  placeholder="Enter first name"
                />
              </ModalField>
              <ModalField>
                <ModalLabel htmlFor="lastName">Last Name</ModalLabel>
                <ModalInput
                  id="lastName"
                  value={newUserData.lastName}
                  onChange={(e) => setNewUserData({...newUserData, lastName: e.target.value})}
                  placeholder="Enter last name"
                />
              </ModalField>
              <ModalField>
                <ModalLabel htmlFor="email">Email</ModalLabel>
                <ModalInput
                  id="email"
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                  placeholder="Enter email address"
                />
              </ModalField>
              <ModalField>
                <ModalLabel htmlFor="password">Password</ModalLabel>
                <ModalInput
                  id="password"
                  type="password"
                  value={newUserData.password}
                  onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                  placeholder="Enter password"
                />
              </ModalField>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={handleCloseModal}>Cancel</CancelButton>
              <AddButton 
                isPrimary 
                onClick={handleCreateUser} 
                disabled={
                  modalLoading ||
                  !newUserData.firstName ||
                  !newUserData.lastName ||
                  !newUserData.email ||
                  !newUserData.password
                }
              >
                {modalLoading ? 'Creating...' : `Create ${newUserType === 'admin' ? 'Administrator' : 'Specialist'}`}
              </AddButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
      {showCaseDetailModal && selectedCaseId && (
        <CaseDetailModal caseId={selectedCaseId} />
      )}
    </React.Fragment>
  );
};

export default AdminDashboard;
